import type { RequestContext } from "../../../application/context/request-context.js";
import type { Minuta, MinutaRevision, MinutaCorrectionRequest } from "../../../domain/report/minuta-domain.js";
import type { MinutaRepository } from "../../../repositories/operational-repositories.js";

export class InMemoryMinutaRepository implements MinutaRepository {
  private minutas = new Map<string, Minuta>();
  private revisions = new Map<string, MinutaRevision>();
  private correctionRequests = new Map<string, MinutaCorrectionRequest>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage !== 'undefined') {
        const storedMinutas = localStorage.getItem('ri_minutas');
        if (storedMinutas) {
          const parsed = JSON.parse(storedMinutas);
          for (const m of parsed) {
            m.createdAt = new Date(m.createdAt);
            m.updatedAt = new Date(m.updatedAt);
            this.minutas.set(m.id, m);
          }
        }
        const storedRevs = localStorage.getItem('ri_minuta_revisions');
        if (storedRevs) {
          const parsed = JSON.parse(storedRevs);
          for (const r of parsed) {
            r.createdAt = new Date(r.createdAt);
            this.revisions.set(r.id, r);
          }
        }
        const storedReqs = localStorage.getItem('ri_minuta_corrections');
        if (storedReqs) {
          const parsed = JSON.parse(storedReqs);
          for (const c of parsed) {
            c.createdAt = new Date(c.createdAt);
            c.updatedAt = new Date(c.updatedAt);
            this.correctionRequests.set(c.id, c);
          }
        }
      }
    } catch (e) {
      console.warn("Failed to load minutas from storage:", e);
    }
  }

  private persistStorage() {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('ri_minutas', JSON.stringify(Array.from(this.minutas.values())));
        localStorage.setItem('ri_minuta_revisions', JSON.stringify(Array.from(this.revisions.values())));
        localStorage.setItem('ri_minuta_corrections', JSON.stringify(Array.from(this.correctionRequests.values())));
      }
    } catch (e) {
      console.warn("Failed to persist minutas to storage:", e);
    }
  }

  async findById(context: RequestContext, id: string): Promise<Minuta | null> {
    this.loadFromStorage();
    return this.minutas.get(id) || null;
  }

  async listAll(context: RequestContext): Promise<readonly Minuta[]> {
    this.loadFromStorage();
    return Array.from(this.minutas.values());
  }

  async findByInspection(context: RequestContext, inspectionId: string): Promise<Minuta | null> {
    // P0-02: deprecated — retorna a primeira Minuta encontrada (compatibilidade).
    this.loadFromStorage();
    for (const minuta of this.minutas.values()) {
      if (minuta.inspectionId === inspectionId) {
        return minuta;
      }
    }
    return null;
  }

  async findByInspectionAndExecutionType(_context: RequestContext, inspectionId: string, executionType: 'PARADA' | 'ROTINA'): Promise<Minuta | null> {
    this.loadFromStorage();
    for (const minuta of this.minutas.values()) {
      if (minuta.inspectionId === inspectionId && minuta.executionType === executionType) {
        return minuta;
      }
    }
    return null;
  }

  async listByInspection(_context: RequestContext, inspectionId: string): Promise<readonly Minuta[]> {
    this.loadFromStorage();
    const result: Minuta[] = [];
    for (const minuta of this.minutas.values()) {
      if (minuta.inspectionId === inspectionId) {
        result.push(minuta);
      }
    }
    return result;
  }

  async findByProjectAndYear(context: RequestContext, projectId: string, year: number): Promise<readonly Minuta[]> {
    this.loadFromStorage();
    const result: Minuta[] = [];
    for (const minuta of this.minutas.values()) {
      if (minuta.projectId === projectId && minuta.createdAt.getFullYear() === year) {
        result.push(minuta);
      }
    }
    return result;
  }

  async save(context: RequestContext, minuta: Minuta): Promise<void> {
    this.minutas.set(minuta.id, { ...minuta });
    this.persistStorage();
  }

  async saveRevision(context: RequestContext, revision: MinutaRevision): Promise<void> {
    this.revisions.set(revision.id, { ...revision });
    this.persistStorage();
  }

  async findRevisionsByMinuta(context: RequestContext, minutaId: string): Promise<readonly MinutaRevision[]> {
    this.loadFromStorage();
    const result: MinutaRevision[] = [];
    for (const rev of this.revisions.values()) {
      if (rev.minutaId === minutaId) {
        result.push(rev);
      }
    }
    return result.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }

  async saveCorrectionRequest(context: RequestContext, request: MinutaCorrectionRequest): Promise<void> {
    this.correctionRequests.set(request.id, { ...request });
    this.persistStorage();
  }

  async findCorrectionRequests(context: RequestContext, minutaId: string): Promise<readonly MinutaCorrectionRequest[]> {
    this.loadFromStorage();
    const result: MinutaCorrectionRequest[] = [];
    for (const req of this.correctionRequests.values()) {
      if (req.minutaId === minutaId) {
        result.push(req);
      }
    }
    return result.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }
}
